import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AdsIndicator from '../../../components/AdsIndicator';
import { useAuth } from '../../../contexts/AuthContext';
import gameService from '../../../utils/gameService';
import { generateGameUrl } from '../../../utils/urlUtils';

const GameCard = ({ game, size = 'default', onFavoriteChange }) => {
  const { user } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorited, setIsFavorited] = useState(game.isFavorited || false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if game is favorited on component mount
  useEffect(() => {
    if (user && game.id) {
      checkFavoriteStatus();
    }
  }, [user, game.id]);

  const checkFavoriteStatus = async () => {
    try {
      const favorites = await gameService.getUserFavorites(user.id);
      if (favorites.success) {
        const isFav = favorites.data.some(fav => fav.game_id === game.id);
        setIsFavorited(isFav);
      }
    } catch (error) {
      console.log('Error checking favorite status:', error);
    }
  };

  const handleFavoriteToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      alert('Please sign in to add favorites');
      return;
    }

    setIsLoading(true);
    try {
      const result = await gameService.toggleFavorite(user.id, game.id);
      if (result.success) {
        setIsFavorited(result.isFavorited);
        if (onFavoriteChange) {
          onFavoriteChange(game.id, result.isFavorited);
        }
      } else {
        console.error('Failed to toggle favorite:', result.error);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRating = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      alert('Please sign in to rate games');
      return;
    }

    setShowRatingModal(true);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowShareModal(true);
  };

  const shareGame = (platform) => {
    const gameUrl = `${window.location.origin}${generateGameUrl(game)}`;
    const shareText = `Check out ${game.title} on Solis Games!`;

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(gameUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(gameUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(gameUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(gameUrl).then(() => {
          alert('Game link copied to clipboard!');
        });
        break;
      case 'native':
        if (navigator.share) {
          navigator.share({
            title: game.title,
            text: shareText,
            url: gameUrl,
          });
        } else {
          // Fallback for browsers that don't support Web Share API
          navigator.clipboard.writeText(gameUrl).then(() => {
            alert('Game link copied to clipboard!');
          });
        }
        break;
    }
    setShowShareModal(false);
  };

  const cardSizes = {
    small: 'w-32 sm:w-40',
    default: 'w-40 sm:w-48 md:w-52',
    large: 'w-48 sm:w-56 md:w-64'
  };

  const imageSizes = {
    small: 'h-20 sm:h-24',
    default: 'h-24 sm:h-28 md:h-32',
    large: 'h-28 sm:h-32 md:h-36'
  };

  return (
    <>
      <div
        className={`${cardSizes[size]} flex-shrink-0 group cursor-pointer`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link to={generateGameUrl(game)} state={{ game }}>
          <div className="relative overflow-hidden rounded-lg bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-gaming-lg hover:scale-105">
            {/* Game Cover */}
            <div className={`relative ${imageSizes[size]} overflow-hidden`}>
              <Image
                src={game.coverImage || game.cover_image_url || game.cover_image}
                alt={game.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />

              {/* Overlay on Hover */}
              {isHovered && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Play"
                    className="transform scale-90 group-hover:scale-100 transition-transform duration-300"
                  >
                    Play
                  </Button>
                </div>
              )}

              {/* Rating Badge */}
              {game.rating && (
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center space-x-1">
                  <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                  <span>{game.rating}</span>
                </div>
              )}

              {/* New Badge */}
              {game.isNew && (
                <div className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded">
                  New
                </div>
              )}

              {/* Progress Bar for Continue Playing */}
              {game.progress && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${game.progress}%` }}
                  />
                </div>
              )}
            </div>

            {/* Game Info */}
            <div className="p-3">
              <h3 className="font-semibold text-foreground text-sm mb-1 truncate">
                {game.title}
              </h3>
              <p className="text-muted-foreground text-xs mb-2 truncate">
                {game.studio}
              </p>

              {/* Genre Tags and Ads Indicator */}
              <div className="flex flex-wrap gap-1 mb-2">
                {game.genres && game.genres.slice(0, 2).map((genre, index) => (
                  <span
                    key={index}
                    className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded"
                  >
                    {genre}
                  </span>
                ))}
                {game.game_type === 'gamedistribution' && (
                  <AdsIndicator size="xs" />
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handleFavoriteToggle}
                    className={`p-1 rounded transition-colors ${isFavorited ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    <Icon name="Heart" size={14} className={isFavorited ? 'fill-current' : ''} />
                  </button>
                  <button
                    onClick={handleRating}
                    className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="Star" size={14} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="Share" size={14} />
                  </button>
                </div>

                {game.playTime && (
                  <span className="text-xs text-muted-foreground">
                    {game.playTime}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRatingModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Rate {game.title}</h3>
            <div className="flex items-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className="text-2xl text-yellow-400 hover:text-yellow-500 transition-colors"
                  onClick={() => {
                    // Handle rating submission here
                    gameService.submitGameRating(user.id, game.id, { rating: star, comment: '' });
                    setShowRatingModal(false);
                  }}
                >
                  ★
                </button>
              ))}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowRatingModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowShareModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Share {game.title}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => shareGame('twitter')}
                className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Twitter" size={20} />
                <span>Twitter</span>
              </button>
              <button
                onClick={() => shareGame('facebook')}
                className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Facebook" size={20} />
                <span>Facebook</span>
              </button>
              <button
                onClick={() => shareGame('linkedin')}
                className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Linkedin" size={20} />
                <span>LinkedIn</span>
              </button>
              <button
                onClick={() => shareGame('copy')}
                className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Copy" size={20} />
                <span>Copy Link</span>
              </button>
              {navigator.share && (
                <button
                  onClick={() => shareGame('native')}
                  className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors col-span-2"
                >
                  <Icon name="Share" size={20} />
                  <span>Share via...</span>
                </button>
              )}
            </div>
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => setShowShareModal(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GameCard;
